import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  background-color: rgba(233, 64, 87, 0.1);
  border-left: 4px solid var(--primary);
  color: var(--primary-dark);
  padding: 16px;
  margin: 16px 0;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ErrorTitle = styled.h4`
  margin: 0 0 8px 0;
  font-weight: 600;
  color: var(--primary-dark);
`;

const ErrorMessage = styled.p`
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

const SupportEmail = styled.a`
  color: var(--primary);
  font-weight: 500;
  text-decoration: underline;
  
  &:hover {
    color: var(--primary-dark);
  }
`;

const ErrorLink = styled(Link)`
  display: inline-block;
  background-color: var(--primary);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  margin-top: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--primary-dark);
    text-decoration: none;
    color: white;
  }
`;

const ErrorHandler = ({ error, clearError }) => {
  const [formattedError, setFormattedError] = useState({
    title: '',
    message: '',
    link: '',
    linkText: '',
  });

  useEffect(() => {
    if (!error) return;

    // Scroll to top when error is displayed
    window.scrollTo({ top: 0, behavior: 'smooth' });

    let errorObj = {
      title: 'Error',
      message: error,
      link: '',
      linkText: '',
      showSupportEmail: false
    };

    // Check if we have an errorTitle prop
    if (typeof error === 'object' && error.errorTitle) {
      errorObj.title = error.errorTitle;
      errorObj.message = error.general || error.message || 'An error occurred';
      
      // Check if the message contains support email reference
      if (errorObj.message.includes('support@dozzy.au')) {
        errorObj.showSupportEmail = true;
        // Replace the email with a placeholder that we'll render as a link
        errorObj.message = errorObj.message.replace(
          'support@dozzy.au', 
          '{{SUPPORT_EMAIL}}'
        );
      }
    }
    // Check if the error is from our Lambda function with special format
    else if (typeof error === 'string' && error.includes('PreSignUp failed with error ACCOUNT_EXISTS_WITH_SOCIAL:')) {
      // Extract provider and email from error message
      const errorParts = error.replace('PreSignUp failed with error ACCOUNT_EXISTS_WITH_SOCIAL:', '').split(':');
      const provider = errorParts[0];
      const email = errorParts[1];

      errorObj = {
        title: 'Account Already Exists',
        message: `It looks like you already have an account with the email ${email}. Please sign in with ${provider} instead of creating a new account.`,
        link: '/login',
        linkText: `Sign in with ${provider}`,
      };
    } else if (typeof error === 'string' && error.includes('PreSignUp failed with error ACCOUNT_EXISTS_WITH_EMAIL:')) {
      // Extract email from error message
      const email = error.replace('PreSignUp failed with error ACCOUNT_EXISTS_WITH_EMAIL:', '');

      errorObj = {
        title: 'Account Already Exists',
        message: `It looks like you already have an account with the email ${email}. Please sign in with your email and password instead of creating a new account.`,
        link: '/login',
        linkText: 'Go to Sign In',
      };
    } else if (typeof error === 'string' && error.includes('PreSignUp failed with error')) {
      // Handle other PreSignUp errors
      errorObj = {
        title: 'Sign Up Error',
        message: error.replace('PreSignUp failed with error ', ''),
      };
    }

    setFormattedError(errorObj);
  }, [error]);

  if (!error) return null;

  return (
    <ErrorContainer>
      <ErrorTitle>{formattedError.title}</ErrorTitle>
      <ErrorMessage>
        {formattedError.showSupportEmail 
          ? formattedError.message.split('{{SUPPORT_EMAIL}}').map((part, index, array) => (
              <React.Fragment key={index}>
                {part}
                {index < array.length - 1 && (
                  <SupportEmail href="mailto:support@dozzy.au">support@dozzy.au</SupportEmail>
                )}
              </React.Fragment>
            ))
          : formattedError.message
        }
      </ErrorMessage>
      {formattedError.link && (
        <ErrorLink to={formattedError.link} onClick={clearError}>
          {formattedError.linkText}
        </ErrorLink>
      )}
      {clearError && !formattedError.link && (
        <button 
          onClick={clearError} 
          style={{ 
            background: 'transparent', 
            border: 'none', 
            color: 'var(--primary-dark)', 
            cursor: 'pointer',
            marginTop: '10px',
            textDecoration: 'underline',
            fontSize: '0.9rem'
          }}
        >
          Dismiss
        </button>
      )}
    </ErrorContainer>
  );
};

export default ErrorHandler;
